import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { StripeSubscription } from '@models/stripe-subscription';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from '@services/atlas-toast.service';
import { LoaderService } from '@services/loader.service';
import { MenuService } from '@services/menu.service';
import { PaymentService } from '@services/payment.service';
import { ShopService } from '@services/shop.service';
import { UserService } from '@services/user.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonRendererComponent } from '@ui/components/atlas-grid/renderer/button-renderer.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { AtlasGridTranslateRendererComponent } from '@ui/components/atlas-grid/renderer/translate-renderer.component';
import { formateDateTime } from '@utils/utils';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-billing-subscriptions',
  templateUrl: './billing-subscriptions.component.html',
  styleUrls: ['./billing-subscriptions.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class BillingSubscriptionsComponent implements OnInit, AfterViewInit {
  @ViewChild('modalDeleteSubscription') modalDeleteSubscription;
  @ViewChild('modalUpgradeSubscription') modalUpgradeSubscription;
  @ViewChild('gridSubscriptions') gridSubscriptions: AtlasGridComponent;

  modalDeleteSubscriptionModal: NgbModalRef;
  modalUpgradeSubscriptionModal: NgbModalRef;

  gridAnimationState = 'hide';

  components = {
    buttonRenderer: ButtonRendererComponent,
    buttonGroupRenderer: ButtonGroupRendererComponent,
    translateRenderer: AtlasGridTranslateRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };

  isHideColumnUserDetails: boolean;
  paginationPageSize = 10;
  tableSubscriptionsColumnDefs = null;
  tableSubscriptionsGridOptions = null;
  tableSubscriptionsData = null;
  tableSubscriptionsLoading: boolean;
  subscriptionToUpgrade = null;
  subscriptionToDelete = null;

  itemUpgrades;
  constructor(
    private shopService: ShopService,
    private paymentService: PaymentService,
    private atlasToastService: AtlasToastService,
    private loaderService: LoaderService,
    private userService: UserService,
    private translateService: TranslateService,
    private menuService: MenuService,
    private modalService: NgbModal
  ) {}

  ngAfterViewInit(): void {
    this.gridSubscriptions.dataIsLoaded.subscribe(() => {
      this.gridAnimationState = 'show';
    });
  }

  ngOnInit(): void {
    this.initManageSubscription();
  }

  initManageSubscription() {
    // Call to show or not show the user detail column
    this.menuService.getMenuEntriesState().subscribe((entries: any) => {
      this.isHideColumnUserDetails = entries.subaccount ? true : false;
      this.initManageSubscriptionTab();
    });
  }

  initManageSubscriptionTab() {
    this.tableSubscriptionsGridOptions = AtlasGridOptions.deserialize({
      pagination: true,
      resizable: false,
      autoHeight: true,
      rowHeight: 90,
      paginationPageSize: this.paginationPageSize,
      loadFunction: this.reloadListSubscriptions.bind(this)
    });
    this.tableSubscriptionsColumnDefs = [
      {
        headerNameTranslate: 'common_description',
        field: 'main',
        autoHeight: true,
        wrapText: true,
        cellStyle: { 'white-space': 'normal', 'line-height': '150%', padding: '0.7em 0' },
        flex: 1,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          contentObservable: params =>
            new Observable(observer => {
              if (!params || !params.data) {
                observer.next('');
                observer.complete();
              } else {
                const subscription: StripeSubscription = params.data;
                const toTranslate = [
                  'subscription_type_' + subscription.category,
                  'subscription_srv_smss_number',
                  'subscription_srv_smss_keyword',
                  'subscription_period_month',
                  'subscription_period_year',
                  'subscription_pack_' + subscription.label
                ];
                if (subscription.monthsInterval !== 1 && subscription.monthsInterval !== 12) {
                  toTranslate.push('subscription_period_' + subscription.monthsInterval + 'months');
                }
                this.translateService.get(toTranslate).subscribe(y => {
                  let ret = '';
                  ret += '<strong>' + y['subscription_type_' + subscription.category] + '</strong><br>';
                  if (subscription.category === 'srv_smss' || subscription.category === 'srv_smsd') {
                    if (subscription.metadata.sim) {
                      ret += '<strong>' + y.subscription_srv_smss_number + '</strong>' + subscription.metadata.sim;
                    }
                    if (subscription.metadata.keyword) {
                      ret += ' <strong>' + y.subscription_srv_smss_keyword + '</strong>' + subscription.metadata.keyword;
                    }
                    if (subscription.metadata.sim || subscription.metadata.keyword) {
                      ret += '<br>';
                    }
                  }
                  let period = y.subscription_period_month;
                  if (subscription.monthsInterval === 12) {
                    period = y.subscription_period_year;
                  } else if (subscription.monthsInterval > 1) {
                    period = y['subscription_period_' + subscription.monthsInterval + 'months'];
                  }
                  ret += y['subscription_pack_' + subscription.label] + ' | ' + subscription.price + ' ' + period;
                  observer.next(ret);
                  observer.complete();
                });
              }
            })
        }
      },
      {
        headerNameTranslate: 'common_status',
        field: 'status',
        autoHeight: true,
        wrapText: true,
        cellStyle: { 'white-space': 'normal', 'line-height': '150%', padding: '0.7em 0' },
        flex: 1,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          contentObservable: params =>
            new Observable(observer => {
              if (!params || !params.data) {
                observer.next('');
                observer.complete();
              } else if (this.isActiveTrialSubscription(params.data)) {
                const note = params.data?.note;
                this.translateService.get(note).subscribe(y => {
                  observer.next(y);
                  observer.complete();
                });
              } else {
                this.translateService
                  .get(['subscription_pack_deleted', 'common_data_expired', 'subscription_pack_expiring'])
                  .subscribe(y => {
                    observer.next(
                      params.data.deleted || params.data.trial
                        ? y.common_data_expired +
                            ' ' +
                            formateDateTime(params.data.nextBillingDate, false) +
                            '<br>' +
                            y.subscription_pack_deleted
                        : y.subscription_pack_expiring + ' ' + formateDateTime(params.data.nextBillingDate, false)
                    );
                    observer.complete();
                  });
              }
            })
        }
      },
      {
        headerNameTranslate: 'settings_subaccounts_table_field_details',
        field: 'userBeneficiary',
        sortable: false,
        wrapText: true,
        hide: this.isHideColumnUserDetails,
        cellRenderer: benf => {
          return this.getUserBeneficiary(benf.data);
        },
        cellStyle: {
          'line-height': '20px'
        }
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        cellClass: 'text-end',
        cellRendererParams: {
          hide: data => !data || data.deleted || this.isActiveTrialSubscription(data),
          items: [
            {
              onClick: e => this.openModalUpgradeSubscription(e),
              label: 'shop_upgrade_sub',
              hide: data => !data || !this.isAllowedUpgrade(data)
            },
            {
              onClick: e => this.openModalSubscriptionDelete(e),
              label: 'subscription_pack_delete'
              // hide: data => !data || data.deleted,
            }
          ]
        },
        maxWidth: 82
      }
    ];
  }

  isAllowedUpgrade(data: any): boolean {
    if (data.category === 'srv_pages') {
      return true;
    }
    return false;
  }

  reloadListSubscriptions(startRow?, endRow?, sort?) {
    this.loaderService.show();
    return this.paymentService
      .getStripeSubscriptions(1 + startRow / (endRow - startRow), endRow - startRow)
      .pipe(finalize(() => this.loaderService.hide()));
  }

  getUserBeneficiary(data?: any) {
    if (data) {
      const userBeneficiary = data.userBeneficiary;
      if (userBeneficiary) {
        if (userBeneficiary.idUserBeneficiary !== this.userService.getSessionUserId()) {
          return this.styleCellBeneficiary(userBeneficiary);
        }
      }
    }
  }

  private styleCellBeneficiary(userBeneficiary): string {
    return (
      '<div style="font-weight: bold;">' +
      userBeneficiary?.loginBeneficiary +
      '</div>' +
      userBeneficiary?.nameBeneficiary +
      ' ' +
      userBeneficiary?.surnameBeneficiary +
      '<br>' +
      userBeneficiary?.companyBeneficiary
    );
  }

  private isActiveTrialSubscription(data) {
    const trial = data;
    let isActive = false;
    if (trial) {
      if (trial.category === 'srv_pages') {
        isActive = trial?.note ? true : false;
      }
    }
    return isActive;
  }

  openModalUpgradeSubscription(data: any): void {
    this.subscriptionToUpgrade = data;

    this.shopService.getItems(this).subscribe(z => {
      this.itemUpgrades = z.data.filter(x => {
        if (this.subscriptionToUpgrade.category === 'srv_pages') {
          return x.label === 'list-pages-shop-items';
        }
        return false;
      });
    });

    this.modalUpgradeSubscriptionModal = this.modalService.open(this.modalUpgradeSubscription, {
      size: 'lg'
    });
    this.modalUpgradeSubscriptionModal.result.then(
      result => {},
      reason => {}
    );
  }

  modalSubscriptionDeleteForm(): void {
    this.gridSubscriptions.showLoading();
    this.paymentService.deleteStripeSubscription(this.subscriptionToDelete).subscribe(
      () => {
        this.atlasToastService.success('subscription_delete_success', null);
        setTimeout(() => {
          // some time needed for stripe..
          this.gridSubscriptions.refresh();
        }, 1000);
      },
      () => {
        this.atlasToastService.error('subscription_delete_error', null);
        this.gridSubscriptions.refresh();
      },
      () => {}
    );
    this.modalDeleteSubscriptionModal.close();
  }

  openModalSubscriptionDelete(data: any): void {
    this.subscriptionToDelete = data;
    this.modalDeleteSubscriptionModal = this.modalService.open(this.modalDeleteSubscription);
    this.modalDeleteSubscriptionModal.result.then(
      result => {},
      reason => {}
    );
  }
}
